import { slugify } from '../utils/lotsogUtils.js';

const NORMALIZE_REGEX = /[^a-z0-9]+/g;
export const LABEL_MAPPING_VERSION = 'dermbench-emergency-v1';

export const DIAGNOSIS_ALIASES = {
  'squamous cell carcinoma': ['scc', 'squamous cell ca', 'squamous carcinoma'],
  'basal cell carcinoma': ['bcc', 'basal cell ca', 'rodent ulcer'],
  melanoma: ['malignant melanoma', 'mm', 'cutaneous melanoma'],
  'atopic dermatitis': ['eczema', 'atopic eczema', 'ad'],
  'seborrheic dermatitis': ['seb derm', 'seborrhoeic dermatitis'],
  'tinea corporis': ['ringworm', 'dermatophytosis'],
  'tinea pedis': ["athlete's foot"],
  psoriasis: ['psoriasis vulgaris', 'plaque psoriasis'],
  urticaria: ['hives', 'urticaria/angioedema'],
  'actinic keratosis': ['ak', 'solar keratosis', 'actinic keratoses'],
  'verruca vulgaris': ['common wart', 'wart', 'verruca'],
  'molluscum contagiosum': ['molluscum'],
  onychomycosis: ['nail fungus', 'tinea unguium'],
  'alopecia areata': ['aa', 'alopecia'],
  vitiligo: ['leukoderma'],
  rosacea: ['acne rosacea'],
  'seborrheic keratosis': ['seborrhoeic keratosis', 'seb k', 'sk'],
  'melanocytic nevus': ['nevus', 'naevus', 'mole', 'benign nevus'],
  'dermatofibroma': ['df'],
  'vascular lesion': ['angioma', 'hemangioma', 'vascular malformation'],
  'benign keratosis': ['benign keratosis like lesion', 'bkl'],
  'acanthosis nigricans': ['an', 'acanthosis'],
  'drug eruption': ['drug rash', 'medication eruption', 'exanthematous drug eruption'],
  'anaphylaxis': ['anaphylactic reaction'],
  angioedema: ['angioedema urticaria'],
  'pemphigus vulgaris': ['pemphigus'],
  'bullous pemphigoid': ['bp'],
  'immune thrombocytopenic purpura': ['itp'],
};

export const EMERGENCY_DERM_ONTOLOGY = {
  emergencyConditions: {
    'necrotizing fasciitis': {
      emergencyTier: 'critical',
      aliases: ['nec fasc', 'flesh eating infection', 'necrotizing soft tissue infection', 'nsti'],
      nearEquivalent: ['cellulitis', 'severe cellulitis', 'soft tissue infection'],
      dangerousConfusions: ['eczema', 'contact dermatitis', 'tinea corporis'],
    },
    'stevens johnson syndrome': {
      emergencyTier: 'critical',
      aliases: ['sjs', 'stevens-johnson syndrome'],
      nearEquivalent: ['toxic epidermal necrolysis', 'drug eruption', 'severe cutaneous adverse reaction'],
      dangerousConfusions: ['viral exanthem', 'urticaria'],
    },
    'toxic epidermal necrolysis': {
      emergencyTier: 'critical',
      aliases: ['ten'],
      nearEquivalent: ['stevens johnson syndrome', 'drug eruption', 'severe cutaneous adverse reaction'],
      dangerousConfusions: ['viral exanthem', 'eczema'],
    },
    dress: {
      emergencyTier: 'high',
      aliases: ['drug reaction with eosinophilia and systemic symptoms'],
      nearEquivalent: ['drug eruption', 'severe cutaneous adverse reaction'],
      dangerousConfusions: ['viral exanthem', 'atopic dermatitis'],
    },
    'meningococcemia': {
      emergencyTier: 'critical',
      aliases: ['meningococcal septicemia', 'meningococcal sepsis'],
      nearEquivalent: ['purpura fulminans', 'sepsis rash'],
      dangerousConfusions: ['petechiae', 'vasculitis'],
    },
    'purpura fulminans': {
      emergencyTier: 'critical',
      aliases: ['fulminant purpura'],
      nearEquivalent: ['meningococcemia', 'sepsis rash'],
      dangerousConfusions: ['vasculitis', 'petechiae'],
    },
    'toxic shock syndrome': {
      emergencyTier: 'high',
      aliases: ['tss'],
      nearEquivalent: ['staphylococcal scalded skin syndrome', 'sepsis rash'],
      dangerousConfusions: ['viral exanthem', 'scarlet fever'],
    },
    'staphylococcal scalded skin syndrome': {
      emergencyTier: 'high',
      aliases: ['ssss'],
      nearEquivalent: ['toxic shock syndrome', 'bullous impetigo'],
      dangerousConfusions: ['atopic dermatitis', 'contact dermatitis'],
    },
    'herpes zoster ophthalmicus': {
      emergencyTier: 'high',
      aliases: ['hzo', 'ophthalmic shingles'],
      nearEquivalent: ['herpes zoster', 'zoster'],
      dangerousConfusions: ['contact dermatitis', 'cellulitis'],
    },
    'disseminated herpes zoster': {
      emergencyTier: 'high',
      aliases: ['disseminated zoster'],
      nearEquivalent: ['herpes zoster', 'varicella zoster infection'],
      dangerousConfusions: ['eczema herpeticum', 'viral exanthem'],
    },
    'eczema herpeticum': {
      emergencyTier: 'high',
      aliases: ['kaposi varicelliform eruption'],
      nearEquivalent: ['disseminated herpes zoster', 'herpes simplex infection'],
      dangerousConfusions: ['atopic dermatitis', 'impetigo'],
    },
    'cellulitis': {
      emergencyTier: 'high',
      aliases: ['soft tissue infection'],
      nearEquivalent: ['necrotizing fasciitis', 'erysipelas'],
      dangerousConfusions: ['stasis dermatitis', 'contact dermatitis'],
    },
    erysipelas: {
      emergencyTier: 'high',
      aliases: [],
      nearEquivalent: ['cellulitis'],
      dangerousConfusions: ['contact dermatitis', 'stasis dermatitis'],
    },
    vasculitis: {
      emergencyTier: 'high',
      aliases: ['cutaneous vasculitis'],
      nearEquivalent: ['purpura fulminans', 'petechial rash'],
      dangerousConfusions: ['urticaria', 'eczema'],
    },
    melanoma: {
      emergencyTier: 'high',
      aliases: ['malignant melanoma', 'mm', 'cutaneous melanoma'],
      nearEquivalent: ['squamous cell carcinoma', 'basal cell carcinoma'],
      dangerousConfusions: ['seborrheic keratosis', 'melanocytic nevus'],
    },
    'squamous cell carcinoma': {
      emergencyTier: 'high',
      aliases: ['scc', 'squamous cell ca', 'squamous carcinoma'],
      nearEquivalent: ['actinic keratosis', 'basal cell carcinoma'],
      dangerousConfusions: ['seborrheic keratosis', 'verruca vulgaris'],
    },
    'basal cell carcinoma': {
      emergencyTier: 'high',
      aliases: ['bcc', 'basal cell ca'],
      nearEquivalent: ['squamous cell carcinoma', 'actinic keratosis'],
      dangerousConfusions: ['seborrheic keratosis', 'melanocytic nevus'],
    },
    'actinic keratosis': {
      emergencyTier: 'high',
      aliases: ['ak', 'solar keratosis', 'actinic keratoses'],
      nearEquivalent: ['squamous cell carcinoma', 'basal cell carcinoma'],
      dangerousConfusions: ['seborrheic keratosis', 'verruca vulgaris'],
    },
    'drug eruption': {
      emergencyTier: 'high',
      aliases: ['drug rash', 'medication eruption'],
      nearEquivalent: ['dress', 'stevens johnson syndrome', 'toxic epidermal necrolysis'],
      dangerousConfusions: ['viral exanthem', 'atopic dermatitis'],
    },
    'acute generalized exanthematous pustulosis': {
      emergencyTier: 'high',
      aliases: ['agep'],
      nearEquivalent: ['drug eruption', 'dress', 'stevens johnson syndrome'],
      dangerousConfusions: ['pustular psoriasis', 'folliculitis'],
    },
    anaphylaxis: {
      emergencyTier: 'critical',
      aliases: ['anaphylactic reaction'],
      nearEquivalent: ['angioedema', 'urticaria'],
      dangerousConfusions: ['urticaria', 'contact dermatitis'],
    },
    angioedema: {
      emergencyTier: 'critical',
      aliases: [],
      nearEquivalent: ['anaphylaxis', 'urticaria'],
      dangerousConfusions: ['contact dermatitis', 'eczema'],
    },
    'pemphigus vulgaris': {
      emergencyTier: 'high',
      aliases: ['pemphigus'],
      nearEquivalent: ['bullous pemphigoid', 'stevens johnson syndrome'],
      dangerousConfusions: ['impetigo', 'atopic dermatitis'],
    },
    'bullous pemphigoid': {
      emergencyTier: 'high',
      aliases: ['bp'],
      nearEquivalent: ['pemphigus vulgaris', 'drug eruption'],
      dangerousConfusions: ['contact dermatitis', 'eczema'],
    },
    'immune thrombocytopenic purpura': {
      emergencyTier: 'high',
      aliases: ['itp'],
      nearEquivalent: ['vasculitis', 'purpura fulminans'],
      dangerousConfusions: ['traumatic purpura', 'eczema'],
    },
    'disseminated gonococcal infection': {
      emergencyTier: 'high',
      aliases: ['dgi'],
      nearEquivalent: ['meningococcemia', 'vasculitis'],
      dangerousConfusions: ['viral exanthem', 'contact dermatitis'],
    },
    'rickettsial rash illness': {
      emergencyTier: 'high',
      aliases: ['rocky mountain spotted fever', 'rmsf'],
      nearEquivalent: ['meningococcemia', 'vasculitis'],
      dangerousConfusions: ['viral exanthem', 'urticaria'],
    },
  },
};

const CONDITION_FAMILY_MAP = {
  melanoma: 'malignant-neoplasm',
  'squamous cell carcinoma': 'malignant-neoplasm',
  'basal cell carcinoma': 'malignant-neoplasm',
  'actinic keratosis': 'precancerous-keratinocyte',
  'seborrheic keratosis': 'benign-keratinocyte',
  'benign keratosis': 'benign-keratinocyte',
  'melanocytic nevus': 'benign-melanocytic',
  vitiligo: 'pigmentary-disorder',
  psoriasis: 'papulosquamous-disorder',
  'atopic dermatitis': 'eczematous-dermatitis',
  'seborrheic dermatitis': 'eczematous-dermatitis',
  urticaria: 'urticarial-disorder',
  'tinea corporis': 'dermatophyte-infection',
  'tinea pedis': 'dermatophyte-infection',
  onychomycosis: 'dermatophyte-infection',
  rosacea: 'facial-inflammatory',
};

function normalizeDiagnosisName(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(NORMALIZE_REGEX, ' ')
    .trim();
}

function buildAliasLookup() {
  const lookup = new Map();
  Object.entries(DIAGNOSIS_ALIASES).forEach(([groundTruth, aliases]) => {
    const normalizedGroundTruth = normalizeDiagnosisName(groundTruth);
    lookup.set(normalizedGroundTruth, normalizedGroundTruth);
    aliases.forEach((alias) => {
      const normalizedAlias = normalizeDiagnosisName(alias);
      lookup.set(normalizedAlias, normalizedGroundTruth);
    });
  });
  return lookup;
}

const ALIAS_LOOKUP = buildAliasLookup();

function buildEmergencyLookup() {
  const canonicalByNormalized = new Map();
  const nearEquivalentLookup = new Map();
  const dangerousConfusionLookup = new Map();
  const emergencyTierLookup = new Map();

  Object.entries(EMERGENCY_DERM_ONTOLOGY.emergencyConditions).forEach(([canonicalLabel, definition]) => {
    const normalizedCanonical = normalizeDiagnosisName(canonicalLabel);
    canonicalByNormalized.set(normalizedCanonical, normalizedCanonical);
    emergencyTierLookup.set(normalizedCanonical, definition?.emergencyTier || 'high');

    (Array.isArray(definition?.aliases) ? definition.aliases : []).forEach((alias) => {
      const normalizedAlias = normalizeDiagnosisName(alias);
      if (normalizedAlias) {
        canonicalByNormalized.set(normalizedAlias, normalizedCanonical);
      }
    });

    nearEquivalentLookup.set(
      normalizedCanonical,
      new Set(
        (Array.isArray(definition?.nearEquivalent) ? definition.nearEquivalent : [])
          .map((value) => normalizeDiagnosisName(value))
          .filter(Boolean)
      )
    );
    dangerousConfusionLookup.set(
      normalizedCanonical,
      new Set(
        (Array.isArray(definition?.dangerousConfusions) ? definition.dangerousConfusions : [])
          .map((value) => normalizeDiagnosisName(value))
          .filter(Boolean)
      )
    );
  });

  return {
    canonicalByNormalized,
    nearEquivalentLookup,
    dangerousConfusionLookup,
    emergencyTierLookup,
  };
}

const EMERGENCY_LOOKUP = buildEmergencyLookup();

export function getConditionSlug(conditionLabel) {
  return slugify(conditionLabel || 'unknown-condition');
}

export function canonicalizeDiagnosisLabel(value) {
  const normalized = normalizeDiagnosisName(value);
  const emergencyCanonical = EMERGENCY_LOOKUP.canonicalByNormalized.get(normalized);
  if (emergencyCanonical) return emergencyCanonical;
  return ALIAS_LOOKUP.get(normalized) || normalized;
}

export function isEmergencyCondition(value) {
  const canonical = canonicalizeDiagnosisLabel(value);
  return EMERGENCY_LOOKUP.emergencyTierLookup.has(canonical);
}

export function getEmergencyConditionTier(value) {
  const canonical = canonicalizeDiagnosisLabel(value);
  return EMERGENCY_LOOKUP.emergencyTierLookup.get(canonical) || null;
}

export function getEmergencyCanonicalConditions() {
  return Object.keys(EMERGENCY_DERM_ONTOLOGY.emergencyConditions);
}

function toCanonicalDiagnosis(value) {
  return canonicalizeDiagnosisLabel(value);
}

function getConditionFamily(value) {
  const canonical = toCanonicalDiagnosis(value);
  return CONDITION_FAMILY_MAP[canonical] || '';
}

function buildNoMatchResult(groundTruth, totalDdxEntries) {
  const groundTruthCanonical = toCanonicalDiagnosis(groundTruth);
  const isEmergencyGroundTruth = isEmergencyCondition(groundTruthCanonical);
  return {
    matched: false,
    strictMatched: false,
    clinicalMatched: false,
    matchType: 'none',
    emergencyMatchType: 'none',
    rankPosition: null,
    inTop1: false,
    inTop3: false,
    inTop5: false,
    inTop7: false,
    groundTruth,
    groundTruthCanonical,
    isEmergencyGroundTruth,
    emergencyTier: getEmergencyConditionTier(groundTruthCanonical),
    dangerousConfusion: false,
    top1Canonical: null,
    matchedCondition: null,
    matchedCanonical: null,
    totalDdxEntries,
  };
}

export function matchDiagnosis(groundTruth, diagnoses, options = {}) {
  const benchmarkProfile = String(options?.benchmarkProfile || 'global').toLowerCase();
  const normalizedGroundTruth = normalizeDiagnosisName(groundTruth);
  const diagnosisList = Array.isArray(diagnoses) ? diagnoses : [];
  const totalDdxEntries = diagnosisList.length;
  if (!normalizedGroundTruth || !totalDdxEntries) {
    return buildNoMatchResult(groundTruth, totalDdxEntries);
  }

  const normalizedGroundAlias = ALIAS_LOOKUP.get(normalizedGroundTruth) || normalizedGroundTruth;
  const groundTruthCanonical = toCanonicalDiagnosis(normalizedGroundAlias);
  const isEmergencyGroundTruth = isEmergencyCondition(groundTruthCanonical);
  const groundFamily = getConditionFamily(normalizedGroundAlias);

  for (let index = 0; index < diagnosisList.length; index += 1) {
    const diagnosis = diagnosisList[index];
    const condition = typeof diagnosis === 'string' ? diagnosis : diagnosis?.condition;
    const normalizedCondition = normalizeDiagnosisName(condition);
    if (!normalizedCondition) continue;
    const matchedCanonical = toCanonicalDiagnosis(normalizedCondition);

    if (normalizedCondition === normalizedGroundTruth) {
      return {
        matched: true,
        strictMatched: true,
        clinicalMatched: true,
        matchType: 'exact',
        emergencyMatchType: 'exact',
        rankPosition: index + 1,
        inTop1: index === 0,
        inTop3: index < 3,
        inTop5: index < 5,
        inTop7: index < 7,
        groundTruth,
        groundTruthCanonical,
        isEmergencyGroundTruth,
        emergencyTier: getEmergencyConditionTier(groundTruthCanonical),
        dangerousConfusion: false,
        top1Canonical:
          diagnosisList.length > 0 ? toCanonicalDiagnosis(diagnosisList[0]?.condition || diagnosisList[0]) : null,
        matchedCondition: condition,
        matchedCanonical,
        totalDdxEntries,
      };
    }

    const normalizedConditionAlias = ALIAS_LOOKUP.get(normalizedCondition) || normalizedCondition;
    if (normalizedConditionAlias === normalizedGroundAlias) {
      return {
        matched: true,
        strictMatched: true,
        clinicalMatched: true,
        matchType: 'alias',
        emergencyMatchType: 'alias',
        rankPosition: index + 1,
        inTop1: index === 0,
        inTop3: index < 3,
        inTop5: index < 5,
        inTop7: index < 7,
        groundTruth,
        groundTruthCanonical,
        isEmergencyGroundTruth,
        emergencyTier: getEmergencyConditionTier(groundTruthCanonical),
        dangerousConfusion: false,
        top1Canonical:
          diagnosisList.length > 0 ? toCanonicalDiagnosis(diagnosisList[0]?.condition || diagnosisList[0]) : null,
        matchedCondition: condition,
        matchedCanonical,
        totalDdxEntries,
      };
    }

    if (benchmarkProfile === 'emergency' && isEmergencyGroundTruth) {
      const nearEquivalentSet = EMERGENCY_LOOKUP.nearEquivalentLookup.get(groundTruthCanonical);
      if (nearEquivalentSet && nearEquivalentSet.has(matchedCanonical)) {
        return {
          matched: true,
          strictMatched: false,
          clinicalMatched: true,
          matchType: 'near_equivalent',
          emergencyMatchType: 'near_equivalent',
          rankPosition: index + 1,
          inTop1: index === 0,
          inTop3: index < 3,
          inTop5: index < 5,
          inTop7: index < 7,
          groundTruth,
          groundTruthCanonical,
          isEmergencyGroundTruth,
          emergencyTier: getEmergencyConditionTier(groundTruthCanonical),
          dangerousConfusion: false,
          top1Canonical:
            diagnosisList.length > 0 ? toCanonicalDiagnosis(diagnosisList[0]?.condition || diagnosisList[0]) : null,
          matchedCondition: condition,
          matchedCanonical,
          totalDdxEntries,
        };
      }
    }

    const conditionFamily = getConditionFamily(normalizedConditionAlias);
    if (groundFamily && conditionFamily && groundFamily === conditionFamily) {
      return {
        matched: true,
        strictMatched: false,
        clinicalMatched: true,
        matchType: 'hierarchy',
        emergencyMatchType: 'hierarchy',
        rankPosition: index + 1,
        inTop1: index === 0,
        inTop3: index < 3,
        inTop5: index < 5,
        inTop7: index < 7,
        groundTruth,
        groundTruthCanonical,
        isEmergencyGroundTruth,
        emergencyTier: getEmergencyConditionTier(groundTruthCanonical),
        dangerousConfusion: false,
        top1Canonical:
          diagnosisList.length > 0 ? toCanonicalDiagnosis(diagnosisList[0]?.condition || diagnosisList[0]) : null,
        matchedCondition: condition,
        matchedCanonical,
        totalDdxEntries,
      };
    }

    if (
      normalizedCondition.includes(normalizedGroundTruth) ||
      normalizedGroundTruth.includes(normalizedCondition)
    ) {
      return {
        matched: true,
        strictMatched: false,
        clinicalMatched: true,
        matchType: 'partial',
        emergencyMatchType: 'partial',
        rankPosition: index + 1,
        inTop1: index === 0,
        inTop3: index < 3,
        inTop5: index < 5,
        inTop7: index < 7,
        groundTruth,
        groundTruthCanonical,
        isEmergencyGroundTruth,
        emergencyTier: getEmergencyConditionTier(groundTruthCanonical),
        dangerousConfusion: false,
        top1Canonical:
          diagnosisList.length > 0 ? toCanonicalDiagnosis(diagnosisList[0]?.condition || diagnosisList[0]) : null,
        matchedCondition: condition,
        matchedCanonical,
        totalDdxEntries,
      };
    }
  }

  const noMatchResult = buildNoMatchResult(groundTruth, totalDdxEntries);
  const top1Canonical =
    diagnosisList.length > 0 ? toCanonicalDiagnosis(diagnosisList[0]?.condition || diagnosisList[0]) : null;
  const dangerousSet = EMERGENCY_LOOKUP.dangerousConfusionLookup.get(noMatchResult.groundTruthCanonical);
  const dangerousConfusion = Boolean(dangerousSet && top1Canonical && dangerousSet.has(top1Canonical));
  return {
    ...noMatchResult,
    dangerousConfusion,
    top1Canonical,
  };
}

function ratio(numerator, denominator) {
  return denominator > 0 ? numerator / denominator : 0;
}

function computeScoresForPredicate(rows, isMatchPredicate) {
  const total = rows.length;
  if (!total) {
    return {
      top1Accuracy: 0,
      top3Accuracy: 0,
      top5Accuracy: 0,
      top7Accuracy: 0,
      missRate: 0,
      avgRankOfCorrectDx: 0,
    };
  }

  const matchedRows = rows.filter((row) => isMatchPredicate(row));
  const matchedRanks = matchedRows.map((row) => Number(row.rankPosition)).filter(Number.isFinite);
  const averageRank =
    matchedRanks.length > 0
      ? matchedRanks.reduce((sum, rank) => sum + rank, 0) / matchedRanks.length
      : 0;

  return {
    top1Accuracy: ratio(rows.filter((row) => row?.inTop1 && isMatchPredicate(row)).length, total),
    top3Accuracy: ratio(rows.filter((row) => row?.inTop3 && isMatchPredicate(row)).length, total),
    top5Accuracy: ratio(rows.filter((row) => row?.inTop5 && isMatchPredicate(row)).length, total),
    top7Accuracy: ratio(rows.filter((row) => row?.inTop7 && isMatchPredicate(row)).length, total),
    missRate: ratio(rows.filter((row) => !isMatchPredicate(row)).length, total),
    avgRankOfCorrectDx: averageRank,
  };
}

export function computeRunScores(matchResults) {
  const rows = Array.isArray(matchResults) ? matchResults : [];
  return computeScoresForPredicate(rows, (row) => Boolean(row?.matched));
}

export function computeRunScoresByMode(matchResults) {
  const rows = Array.isArray(matchResults) ? matchResults : [];
  const strict = computeScoresForPredicate(rows, (row) =>
    row?.strictMatched !== undefined ? Boolean(row.strictMatched) : Boolean(row?.matched)
  );
  const clinical = computeScoresForPredicate(rows, (row) =>
    row?.clinicalMatched !== undefined ? Boolean(row.clinicalMatched) : Boolean(row?.matched)
  );
  return { strict, clinical };
}

export function computeEmergencySafetyScores(matchResults) {
  const rows = Array.isArray(matchResults) ? matchResults : [];
  const emergencyRows = rows.filter((row) => row?.isEmergencyGroundTruth);
  const denominator = emergencyRows.length;
  if (!denominator) {
    return {
      dangerousMissRate: 0,
      emergencyRecallAt7: 0,
      dangerousMissCount: 0,
      emergencyCaseCount: 0,
      dangerousConfusionCount: 0,
    };
  }

  const dangerousMissCount = emergencyRows.filter((row) => !row?.clinicalMatched).length;
  const emergencyRecallCount = emergencyRows.filter((row) => row?.clinicalMatched && row?.inTop7).length;
  const dangerousConfusionCount = emergencyRows.filter((row) => row?.dangerousConfusion).length;

  return {
    dangerousMissRate: ratio(dangerousMissCount, denominator),
    emergencyRecallAt7: ratio(emergencyRecallCount, denominator),
    dangerousMissCount,
    emergencyCaseCount: denominator,
    dangerousConfusionCount,
  };
}

export function computePerConditionScores(resultsByCondition) {
  const output = {};
  Object.entries(resultsByCondition || {}).forEach(([condition, rows]) => {
    const runScores = computeRunScores(rows);
    output[condition] = {
      top1: runScores.top1Accuracy,
      top3: runScores.top3Accuracy,
      top5: runScores.top5Accuracy,
      top7: runScores.top7Accuracy,
      avgRank: runScores.avgRankOfCorrectDx,
      imageCount: Array.isArray(rows) ? rows.length : 0,
    };
  });
  return output;
}

export function computePerConditionScoresByMode(resultsByCondition) {
  const output = {};
  Object.entries(resultsByCondition || {}).forEach(([condition, rows]) => {
    output[condition] = computeRunScoresByMode(rows);
  });
  return output;
}

export function computePerConditionEmergencySafetyScores(resultsByCondition) {
  const output = {};
  Object.entries(resultsByCondition || {}).forEach(([condition, rows]) => {
    output[condition] = computeEmergencySafetyScores(rows);
  });
  return output;
}
